// Collapsible upload panel — description input, AI summary checkbox, file picker

import { FileCode } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useUploadSite } from '../../hooks/use-hosted-sites'

interface UploadPanelProps {
  isDark: boolean
  description: string
  setDescription: (v: string) => void
  autoSummary: boolean
  setAutoSummary: (v: boolean) => void
  uploadSite: ReturnType<typeof useUploadSite>
  // fileInputRef stays in orchestrator so handleUpload can reset input.value after upload
  fileInputRef: React.RefObject<HTMLInputElement | null>
  onClose: () => void
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export function UploadPanel({
  isDark,
  description,
  setDescription,
  autoSummary,
  setAutoSummary,
  uploadSite,
  fileInputRef,
  onClose,
  onUpload,
}: UploadPanelProps) {
  return (
    <div
      className={cn(
        'border-b px-4 py-3',
        isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
      )}
    >
      <div className="mx-auto flex max-w-xl flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-2">
          <label
            className={cn(
              'block text-xs font-medium',
              isDark ? 'text-neutral-300' : 'text-neutral-600',
            )}
          >
            Description (required)
          </label>
          <input
            type="text"
            placeholder="e.g. Q1 Revenue Dashboard"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={cn(
              'w-full rounded-lg border px-3 py-1.5 text-xs outline-none',
              isDark
                ? 'border-white/[0.06] bg-surface-2 text-neutral-200 placeholder-neutral-500'
                : 'border-neutral-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400',
            )}
          />
        </div>
        <label
          className={cn(
            'flex items-center gap-1.5 text-xs',
            isDark ? 'text-neutral-400' : 'text-neutral-500',
          )}
        >
          <input
            type="checkbox"
            checked={autoSummary}
            onChange={(e) => setAutoSummary(e.target.checked)}
            className="rounded"
          />
          AI Summary
        </label>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadSite.isPending || description.trim().length === 0}
          className={cn(
            'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium',
            uploadSite.isPending
              ? 'cursor-wait bg-brand-600/50 text-white/50'
              : 'bg-brand-600 text-white hover:bg-brand-500',
          )}
        >
          <FileCode className="h-3.5 w-3.5" />
          {uploadSite.isPending ? 'Publishing...' : 'Publish .html or .zip'}
        </button>
        <button
          onClick={onClose}
          className={cn(
            'rounded-lg px-3 py-1.5 text-xs',
            isDark
              ? 'text-neutral-400 hover:text-neutral-200'
              : 'text-neutral-500 hover:text-neutral-700',
          )}
        >
          Cancel
        </button>
      </div>
      {uploadSite.isError && (
        <p className="mt-2 text-center text-xs text-red-500">
          {(uploadSite.error as Error).message}
        </p>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".html,.htm,.zip"
        onChange={onUpload}
        className="hidden"
      />
    </div>
  )
}
